import React, { useState } from "react";
import {
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronRight,
  X,
  Eye,
  EyeOff,
} from "lucide-react";

const ChurchDirectorySection = ({
  directory,
  expandedDirectory,
  setExpandedDirectory,
  handleEditDirectory,
  handleDeleteDirectory,
  toggleExpanded,
  // Modal props
  showDirectoryModal,
  setShowDirectoryModal,
  editingDirectory,
  setEditingDirectory,
  directoryForm,
  setDirectoryForm,
  directoryLoading,
  handleDirectorySubmit,
}) => {
  const [showAddMemberPrompt, setShowAddMemberPrompt] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDirectoryForm((prev) => ({
      ...prev,
      primaryMember: {
        ...prev.primaryMember,
        [name]: value,
      },
    }));
  };

  const handleTypeChange = (type) => {
    setDirectoryForm((prev) => ({
      ...prev,
      type: type,
      additionalMembers: [],
    }));
    setShowAddMemberPrompt(false);
  };

  const handleAddFamilyMember = () => {
    setDirectoryForm((prev) => ({
      ...prev,
      additionalMembers: [
        ...prev.additionalMembers,
        {
          id: Date.now(),
          name: "",
          dob: "",
          gender: "Male",
          contact_number: "",
          email: "",
        },
      ],
    }));
    setShowAddMemberPrompt(false);
  };

  const handleRemoveFamilyMember = (id) => {
    setDirectoryForm((prev) => ({
      ...prev,
      additionalMembers: prev.additionalMembers.filter((m) => m.id !== id),
    }));
  };

  const handleAdditionalMemberChange = (id, field, value) => {
    setDirectoryForm((prev) => ({
      ...prev,
      additionalMembers: prev.additionalMembers.map((m) =>
        m.id === id ? { ...m, [field]: value } : m,
      ),
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US");
  };

  const closeModal = () => {
    setShowDirectoryModal(false);
    setEditingDirectory(null);
    setDirectoryForm({
      type: "individual",
      primaryMember: {
        name: "",
        dob: "",
        gender: "Male",
        contact_number: "",
        email: "",
        address: "",
      },
      additionalMembers: [],
    });
    setShowAddMemberPrompt(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
          Church Directory
        </h2>
        <button
          onClick={() => {
            setEditingDirectory(null);
            setDirectoryForm({
              type: "individual",
              primaryMember: {
                name: "",
                dob: "",
                gender: "Male",
                contact_number: "",
                email: "",
                address: "",
              },
              additionalMembers: [],
            });
            setShowDirectoryModal(true);
          }}
          className="bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center text-sm sm:text-base"
        >
          <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
          Add new member
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {directory.length === 0 ? (
          <div className="text-center py-12 px-4">
            <p className="text-gray-500 text-lg">No member details added yet</p>
            <p className="text-gray-400 text-sm mt-2">
              Click "Add new member" to get started
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      S. No.
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Family/Individual
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      DOB
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gender
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Number
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email ID
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {directory.map((entry, index) => {
                const primaryMember =
                  entry.members?.find((m) => m.is_primary) ||
                  entry.members?.[0];
                const isExpanded = expandedDirectory.has(entry.id);
                const hasMultipleMembers =
                  !entry.is_individual && entry.members?.length > 1;

                return (
                  <React.Fragment key={entry.id}>
                    <tr
                      className={
                        hasMultipleMembers
                          ? "cursor-pointer hover:bg-gray-50"
                          : ""
                      }
                    >
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{index + 1}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            entry.is_individual
                              ? "bg-blue-100 text-blue-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {entry.is_individual ? "Individual" : "Family"}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {primaryMember?.name || "N/A"}
                          </div>
                          {hasMultipleMembers && (
                            <div className="text-xs text-gray-600 cursor-pointer font-medium">
                              +{entry.members.length - 1} more
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(primaryMember?.dob)}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {primaryMember?.gender || "N/A"}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {primaryMember?.contact_number || "N/A"}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <div className="text-sm text-gray-900 truncate max-w-xs">
                          {primaryMember?.email || "N/A"}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditDirectory(entry)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDirectory(entry)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              toggleExpanded(
                                entry.id,
                                expandedDirectory,
                                setExpandedDirectory,
                              )
                            }
                            className="text-green-600 hover:text-green-900 p-1"
                            title={
                              entry.is_individual
                                ? isExpanded
                                  ? "Hide member's details"
                                  : "View member's details"
                                : isExpanded
                                  ? "Hide family's details"
                                  : "View family's details"
                            }
                          >
                            {isExpanded ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded row showing all members */}
                    {isExpanded && (
                      <tr>
                        <td
                          colSpan="8"
                          className="px-3 sm:px-6 py-4 bg-gray-50"
                        >
                          <div className="space-y-4">
                            {entry.members?.map((member, idx) => (
                              <div
                                key={member.id}
                                className="bg-white p-4 rounded-lg shadow-sm border border-gray-200"
                              >
                                <div className="text-sm font-semibold text-gray-700 mb-3">
                                  {entry.is_individual
                                    ? "Member Details"
                                    : member.is_primary
                                      ? "Primary Member"
                                      : `Family Member ${idx}`}
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  <div>
                                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                                      Name
                                    </span>
                                    <p className="text-sm text-gray-900">
                                      {member.name}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                                      DOB
                                    </span>
                                    <p className="text-sm text-gray-900">
                                      {formatDate(member.dob)}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                                      Gender
                                    </span>
                                    <p className="text-sm text-gray-900">
                                      {member.gender}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                                      Contact
                                    </span>
                                    <p className="text-sm text-gray-900">
                                      {member.contact_number || "N/A"}
                                    </p>
                                  </div>
                                  <div className="md:col-span-2">
                                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                                      Email
                                    </span>
                                    <p className="text-sm text-gray-900">
                                      {member.email || "N/A"}
                                    </p>
                                  </div>
                                  <div className="md:col-span-2">
                                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                                      Address
                                    </span>
                                    <p className="text-sm text-gray-900">
                                      {member.address || "N/A"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Expandable List View */}
        <div className="md:hidden">
          {directory.map((entry, index) => {
            const isExpanded = expandedDirectory.has(entry.id);
            const primaryMember =
              entry.members?.find((m) => m.is_primary) || entry.members?.[0];
            return (
              <div
                key={entry.id}
                className="border-b border-gray-200 last:border-b-0"
              >
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() =>
                    toggleExpanded(
                      entry.id,
                      expandedDirectory,
                      setExpandedDirectory,
                    )
                  }
                >
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                      S. No. {index + 1}
                    </div>
                    <h3 className="text-sm font-medium text-gray-900">
                      {primaryMember?.name || "N/A"}
                    </h3>
                    <div className="mt-1">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          entry.is_individual
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {entry.is_individual ? "Individual" : "Family"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditDirectory(entry);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDirectory(entry);
                        }}
                        className="text-red-600 hover:text-red-900 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    {entry.members?.map((member, idx) => (
                      <div
                        key={member.id}
                        className="border-t border-gray-100 pt-3"
                      >
                        <div className="text-xs font-semibold text-gray-700 mb-2">
                          {member.is_primary
                            ? "Primary Member"
                            : `Family Member ${idx}`}
                        </div>
                        <div className="space-y-2">
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Name:
                            </span>
                            <p className="text-sm text-gray-900 mt-1">
                              {member.name}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                              DOB:
                            </span>
                            <p className="text-sm text-gray-900 mt-1">
                              {formatDate(member.dob)}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Gender:
                            </span>
                            <p className="text-sm text-gray-900 mt-1">
                              {member.gender}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Contact:
                            </span>
                            <p className="text-sm text-gray-900 mt-1">
                              {member.contact_number || "N/A"}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Email:
                            </span>
                            <p className="text-sm text-gray-900 mt-1">
                              {member.email || "N/A"}
                            </p>
                          </div>
                          {member.address && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Address:
                              </span>
                              <p className="text-sm text-gray-900 mt-1">
                                {member.address}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showDirectoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-2xl mx-auto max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingDirectory ? "Edit Directory Entry" : "Add New Member"}
            </h3>
            <form onSubmit={handleDirectorySubmit} className="space-y-4">
              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Adding details for: *
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="type"
                      value="individual"
                      checked={directoryForm.type === "individual"}
                      onChange={(e) => handleTypeChange(e.target.value)}
                      className="mr-2"
                    />
                    Individual
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="type"
                      value="family"
                      checked={directoryForm.type === "family"}
                      onChange={(e) => handleTypeChange(e.target.value)}
                      className="mr-2"
                    />
                    Family
                  </label>
                </div>
              </div>

              {/* Primary Member Fields */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  {directoryForm.type === "family"
                    ? "Primary Member Details"
                    : "Member Details"}
                </h4>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      2. Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={directoryForm.primaryMember.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      3. DOB *
                    </label>
                    <input
                      type="date"
                      name="dob"
                      value={directoryForm.primaryMember.dob}
                      onChange={handleInputChange}
                      required
                      lang="en-US"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      4. Gender *
                    </label>
                    <select
                      name="gender"
                      value={directoryForm.primaryMember.gender}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      5. Contact Number *
                    </label>
                    <input
                      type="tel"
                      name="contact_number"
                      value={directoryForm.primaryMember.contact_number}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      6. Email ID *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={directoryForm.primaryMember.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      7. Address *
                    </label>
                    <textarea
                      name="address"
                      value={directoryForm.primaryMember.address}
                      onChange={handleInputChange}
                      required
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Family Members */}
              {directoryForm.type === "family" && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Want to add another family member?
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="addMember"
                          value="yes"
                          checked={showAddMemberPrompt}
                          onChange={() => setShowAddMemberPrompt(true)}
                          className="mr-2"
                        />
                        Yes
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="addMember"
                          value="no"
                          checked={!showAddMemberPrompt}
                          onChange={() => setShowAddMemberPrompt(false)}
                          className="mr-2"
                        />
                        No
                      </label>
                    </div>
                  </div>

                  {showAddMemberPrompt &&
                    directoryForm.additionalMembers.length === 0 && (
                      <button
                        type="button"
                        onClick={handleAddFamilyMember}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center text-sm"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Family Member
                      </button>
                    )}

                  {directoryForm.additionalMembers.map((member, index) => (
                    <div
                      key={member.id}
                      className="border-t border-gray-200 pt-4 mt-4"
                    >
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-semibold text-gray-700">
                          Family Member {index + 2}
                        </h4>
                        <button
                          type="button"
                          onClick={() => handleRemoveFamilyMember(member.id)}
                          className="text-red-600 hover:text-red-900 p-1"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Name *
                          </label>
                          <input
                            type="text"
                            value={member.name}
                            onChange={(e) =>
                              handleAdditionalMemberChange(
                                member.id,
                                "name",
                                e.target.value,
                              )
                            }
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            DOB *
                          </label>
                          <input
                            type="date"
                            value={member.dob}
                            onChange={(e) =>
                              handleAdditionalMemberChange(
                                member.id,
                                "dob",
                                e.target.value,
                              )
                            }
                            required
                            lang="en-US"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Gender *
                          </label>
                          <select
                            value={member.gender}
                            onChange={(e) =>
                              handleAdditionalMemberChange(
                                member.id,
                                "gender",
                                e.target.value,
                              )
                            }
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Contact Number (Optional)
                          </label>
                          <input
                            type="tel"
                            value={member.contact_number}
                            onChange={(e) =>
                              handleAdditionalMemberChange(
                                member.id,
                                "contact_number",
                                e.target.value,
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email ID (Optional)
                          </label>
                          <input
                            type="email"
                            value={member.email}
                            onChange={(e) =>
                              handleAdditionalMemberChange(
                                member.id,
                                "email",
                                e.target.value,
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                        </div>
                      </div>

                      {/* Ask about adding another member after each member */}
                      {index === directoryForm.additionalMembers.length - 1 && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Want to add another family member?
                          </label>
                          <div className="flex space-x-4">
                            <button
                              type="button"
                              onClick={handleAddFamilyMember}
                              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm"
                            >
                              Yes
                            </button>
                            <button
                              type="button"
                              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors text-sm"
                            >
                              No
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={directoryLoading}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {directoryLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {editingDirectory ? "Saving..." : "Adding..."}
                    </>
                  ) : editingDirectory ? (
                    "Save Changes"
                  ) : (
                    "Add Member"
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChurchDirectorySection;
